import { Box, Flex } from "@nulogy/components";
import WorkOrderFlag from "components/common/Scheduler/SchedulerRenderers/components/blocks/WorkOrderFlag";
import ThinDivider from "components/common/ThinDivider";
import { formatDate, formatProductionRate } from "components/helpers/format";
import { getWorkOrderPriorityLabel } from "components/pages/schedule/helpers/workOrderCardHelper";
import NotesIndicator from "components/pages/schedule/NotesIndicator";
import DraggableWorkOrderCard from "components/pages/schedule/workOrderCards/DraggableWorkOrderCard";
import LineTypePreferences from "components/pages/schedule/workOrderCards/LineTypePreferences";
import WorkOrderCardHeader from "components/pages/schedule/workOrderCards/WorkOrderCardHeader";
import WorkOrderCardInformation from "components/pages/schedule/workOrderCards/WorkOrderCardInformation";
import Codenames from "policies/Codenames";
import React from "react";
import WorkOrderCardStatus from "./WorkOrderCardStatus";
import WorkOrderCardWrapper from "./WorkOrderCardWrapper";

export default function WorkOrderCard({
  workOrder,
  expanded,
  onToggleExpandWorkOrder,
  layoutSettingData,
  showOptions = true,
  highlightCustomWorkOrderFieldsIndicator = false,
}) {
  const hasLineTypePreference = workOrder.lineTypePreferencePrimary || workOrder.lineTypePreferenceSecondary;

  const layoutSetting = layoutSettingData.workOrderLayoutSetting;

  const recommendedProductionRate = workOrder.itemRecommendation?.recommendedProductionRate;
  const recommendedPeople = workOrder.itemRecommendation?.recommendedPeople;

  const customFieldEntries = buildCustomFieldEntries(
    workOrder,
    layoutSetting?.customWorkOrderFields,
    layoutSettingData.customWorkOrderFields,
  );

  const expandedFields = [
    { label: "Work order ID", settingKey: "workOrderId", value: workOrder.externalId },
    { label: "Work order description", settingKey: "workOrderDescription", value: workOrder.description },
    {
      label: "Work order priority",
      settingKey: "workOrderPriority",
      value: getWorkOrderPriorityLabel(workOrder.workOrderPriority),
    },
    {
      label: "Material availability date",
      settingKey: "materialAvailabilityDate",
      value: formatDate(workOrder.materialAvailabilityDate),
    },
    {
      label: `Planned start (from ${Codenames.PackmanagerShort})`,
      settingKey: "plannedStartDate",
      value: formatDate(workOrder.startAt),
    },
    {
      label: `Planned end (from ${Codenames.PackmanagerShort})`,
      settingKey: "plannedEndDate",
      value: formatDate(workOrder.endAt),
    },
    ...customFieldEntries,
    { label: "Reference 1", settingKey: "reference1", value: workOrder.reference1 },
    { label: "Reference 2", settingKey: "reference2", value: workOrder.reference2 },
    { label: "Reference 3", settingKey: "reference3", value: workOrder.reference3 },
    { label: "Reference 4", settingKey: "reference4", value: workOrder.reference4 },
    { label: "Reference 5", settingKey: "reference5", value: workOrder.reference5 },
    { label: "Item class", settingKey: "itemClass", value: workOrder.itemClass?.name },
    { label: "Item family", settingKey: "itemFamily", value: workOrder.itemFamily?.name },
    { label: "Item type", settingKey: "itemType", value: workOrder.itemType?.name },
    { label: "Item category", settingKey: "itemCategory", value: workOrder.itemCategory?.name },
    { label: "Service category", settingKey: "serviceCategory", value: workOrder.serviceCategory?.name },
    {
      label: "Standard production rate",
      settingKey: "standardProductionRate",
      value: formatProductionRate({
        productionRate: workOrder.standardProductionRate,
        unitOfMeasure: workOrder.unitOfMeasure,
      }),
    },
    {
      label: "Recommended production rate",
      settingKey: "recommendedProductionRate",
      value: formatProductionRate({
        productionRate: recommendedProductionRate,
        unitOfMeasure: workOrder.unitOfMeasure,
      }),
    },
    { label: "Standard people", settingKey: "standardPeople", value: workOrder.standardPeople },
    { label: "Recommended people", settingKey: "recommendedPeople", value: recommendedPeople },
  ];

  return (
    <WorkOrderCardWrapper workOrderId={workOrder.id}>
      <DraggableWorkOrderCard workOrder={workOrder}>
        <WorkOrderCardStatus workOrder={workOrder} />
        {workOrder.notes && <NotesIndicator />}
        <Flex flexDirection="column" gap="x1">
          <WorkOrderCardHeader
            expanded={expanded}
            expandedFields={expandedFields}
            highlightCustomWorkOrderFieldsIndicator={highlightCustomWorkOrderFieldsIndicator}
            layoutSetting={layoutSetting}
            onToggleExpandWorkOrder={onToggleExpandWorkOrder}
            showOptions={showOptions}
            workOrder={workOrder}
          />
          <WorkOrderCardInformation
            expanded={expanded}
            expandedFields={expandedFields}
            layoutSetting={layoutSetting}
            workOrder={workOrder}
          />

          {(workOrder.flag || hasLineTypePreference) && (
            <Box>
              <ThinDivider marginTop="0" />
              <Flex alignItems="center" flexDirection="row">
                <WorkOrderFlag flag={workOrder.flag} />
                <LineTypePreferences workOrder={workOrder} />
              </Flex>
            </Box>
          )}
        </Flex>
      </DraggableWorkOrderCard>
    </WorkOrderCardWrapper>
  );
}

// Build dynamic custom field entries for the work order card (only for enabled fields)
function buildCustomFieldEntries(workOrder, customWorkOrderFieldSettings, customWorkOrderFields) {
  if (!customWorkOrderFieldSettings || !customWorkOrderFields) return [];

  const enabledFieldIds = customWorkOrderFieldSettings.filter((f) => f.enabled).map((f) => f.fieldId);

  return enabledFieldIds.map((fieldId) => {
    const fieldDefinition = customWorkOrderFields.find((f) => f.id === fieldId);
    const selection = workOrder.customWorkOrderFieldSelections?.find((s) => s.customWorkOrderField?.id === fieldId);

    return {
      label: fieldDefinition?.label,
      value: selection?.customWorkOrderFieldValue?.description,
    };
  });
}
